import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  Camera, Search, MessageSquare, CreditCard, Star, AlertOctagon,
  Briefcase, Globe, ShieldCheck, Crown, Sliders, Users, Sparkles, FileDown,
} from "lucide-react";

// Section icons + counts per role. Section copy comes from i18n guides.<role>.*
export const GUIDE_ROLE_META = {
  client: { Icon: Camera, sectionIcons: [Camera, Search, MessageSquare, CreditCard, Star], sectionCount: 5 },
  provider: { Icon: Briefcase, sectionIcons: [Briefcase, Globe, ShieldCheck, Search, Crown, CreditCard, AlertOctagon], sectionCount: 7 },
  admin: { Icon: Sliders, sectionIcons: [Users, Briefcase, ShieldCheck, Sliders, Sparkles, FileDown], sectionCount: 6 },
};

// Provider-guide section numbers that belong to a flagged feature, hidden when
// that flag is off. 1-indexed, matching the guides.<role>.s<n>Title i18n keys.
export const FEATURE_SECTIONS = {
  terminal: [6, 7],
};

/** Resolve the guide role for the current user (admin > provider > client). */
export function guideRoleFor(user) {
  if (user?.profile?.is_admin) return "admin";
  if (user?.profile?.role === "provider") return "provider";
  return "client";
}

/**
 * The guide body for a single role — title, section cards and the help CTA.
 * Shared by the standalone /guides page and the dashboard GuideModal so there
 * is one source of truth and only ever the relevant role is shown.
 */
export default function GuideContent({ role }) {
  const { t } = useTranslation();
  const { features } = useAuth();
  const r = (role || "client").toLowerCase();
  const meta = GUIDE_ROLE_META[r] || GUIDE_ROLE_META.client;
  const Icon = meta.Icon;
  // The provider guide's last two sections (s6 "Charge with FixyTerminal", s7
  // "FixyTerminal payments") exist only while the feature does. Dropping the
  // tail keeps sections 1-5 numbered as their copy reads.
  const hidden = r === "provider" && !features?.terminal ? FEATURE_SECTIONS.terminal : [];
  const sections = Array.from({ length: meta.sectionCount }, (_, i) => i)
    .filter((i) => !hidden.includes(i + 1))
    .map((i) => ({
      Icon: meta.sectionIcons[i],
      title: t(`guides.${r}.s${i + 1}Title`),
      body: t(`guides.${r}.s${i + 1}Body`),
    }));

  return (
    <div data-testid={`guide-content-${r}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="text-hive-ink" />
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-worker">{t(`guides.${r}.title`)}</h1>
      </div>
      <p className="text-smoke text-lg">{t(`guides.${r}.subtitle`)}</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {sections.map((s, i) => {
          const I = s.Icon;
          return (
            <div key={i} className="card-base p-5" data-testid={`guide-${r}-${i}`}>
              <I size={22} className="text-hive-ink" />
              <h3 className="mt-2 font-display font-bold text-worker">{s.title}</h3>
              <p className="mt-2 text-sm text-smoke leading-relaxed">{s.body}</p>
            </div>
          );
        })}
      </div>

      <div className="card-base p-5 bg-linen border-sand mt-8" data-testid="guide-help-cta">
        <p className="font-display font-semibold text-worker">{t("guides.stuckTitle")}</p>
        <p className="text-sm text-smoke mt-1">
          <Trans
            i18nKey="guides.stuckBody"
            components={{ a: <a className="text-hive-ink hover:underline" href="mailto:support@fixydany.com" /> }}
          />
        </p>
      </div>
    </div>
  );
}
